import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ScoreRing } from "@/components/ScoreRing";
import { Check, ArrowRight, Sparkles, LogOut, Loader2, Camera } from "lucide-react";

export default function CandidateHome() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const photoRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const loadProfile = () => api.get("/candidate/profile").then((r) => setProfile(r.data)).catch(() => nav("/app/upload"));
  useEffect(() => { loadProfile().finally(() => setLoading(false)); }, [nav]); // eslint-disable-line

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingPhoto(true);
    try { const fd = new FormData(); fd.append("file", f);
      await api.post("/candidate/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Fotografia pridaná"); await loadProfile();
    } catch (err) { toast.error("Nahranie zlyhalo, skúste iný obrázok"); }
    finally { setUploadingPhoto(false); if (photoRef.current) photoRef.current.value = ""; }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-blue-700" /></div>;
  if (!profile) return null;

  const firstName = (profile.name || user?.name || "").split(" ")[0];
  const topPos = (profile.suitable_positions || [])[0];

  return (
    <div className="px-6 pt-12">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-500 text-sm">Dobrý deň 👋</p>
          <h1 className="font-head text-2xl font-bold tracking-tight">{firstName}</h1>
        </div>
        <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }} className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 transition-colors">
          <LogOut size={18} />
        </button>
      </div>

      <div className="mt-6 rounded-3xl bg-white border border-slate-200 shadow-sm p-6 flex flex-col items-center fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Tvoj Career Passport</p>
        <div className="mt-4"><ScoreRing value={profile.career_score || 0} size={150} /></div>
      </div>

      {!profile.photo_url && (
        <div className="mt-5 rounded-3xl bg-amber-50 border border-amber-200 p-5 fade-up" data-testid="no-photo-prompt">
          <p className="text-sm text-amber-900 leading-relaxed">
            Váš profil je pripravený. K profilu sa nám nepodarilo priradiť fotografiu z CV. Pridanie profesionálnej profilovej fotografie môže pomôcť vášmu profilu pôsobiť osobnejšie a zvýšiť šancu, že zaujme recruiterov.
          </p>
          <input ref={photoRef} type="file" accept="image/*" hidden data-testid="candidate-photo-input" onChange={uploadPhoto} />
          <button data-testid="add-photo-btn" onClick={() => photoRef.current?.click()} disabled={uploadingPhoto}
            className="mt-3 inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors active:scale-95 disabled:opacity-60">
            {uploadingPhoto ? <Loader2 className="animate-spin" size={16} /> : <Camera size={16} />} Pridať profilovú fotografiu
          </button>
        </div>
      )}

      <div className="mt-5 rounded-3xl bg-white border border-slate-200 shadow-sm p-6 fade-up">
        <h2 className="font-head font-semibold text-lg">Najsilnejšie oblasti</h2>
        <div className="mt-3 space-y-2.5">
          {(profile.strongest_areas || []).slice(0, 4).map((a) => (
            <div key={a} className="flex items-center gap-2.5">
              <div className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center"><Check size={14} className="text-emerald-600" /></div>
              <span className="text-slate-700 font-medium">{a}</span>
            </div>
          ))}
        </div>
      </div>

      {topPos && (
        <div className="mt-5 rounded-3xl bg-blue-700 text-white p-6 fade-up">
          <div className="flex items-center gap-2 text-blue-200"><Sparkles size={16} /><span className="text-xs font-bold uppercase tracking-[0.15em]">AI odporúčanie</span></div>
          <p className="mt-3 text-lg font-head font-semibold leading-snug">Si vhodný na pozíciu „{topPos.title}"</p>
          <p className="mt-1 text-blue-100 text-sm">Zhoda {topPos.match}% podľa tvojho profilu.</p>
        </div>
      )}

      <button data-testid="view-profile-btn" onClick={() => nav("/app/profile")}
        className="mt-5 w-full flex items-center justify-center gap-2 bg-white text-slate-800 border border-slate-300 hover:bg-slate-50 rounded-full px-6 py-4 font-semibold transition-colors active:scale-95">
        Zobraziť profil <ArrowRight size={18} />
      </button>
    </div>
  );
}
